import { useState, useCallback } from "react";
import { generateSessionId } from "../utils/utils";
import { 
  ModalState, 
  PlaceholderModalState, 
  DeleteModalState, 
  PreviewModalState, 
  StandaloneModalState 
} from "./types";

// Hook for managing modal states
export const useModalStates = () => {
  // Additional modal states that were missing
  const [isResetModalVisible, setIsResetModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isNewTreeModalVisible, setIsNewTreeModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [modalState, setModalState] = useState<ModalState>({
    visible: false,             // Changed from isModalVisible
    modalTitle: "Add New Node",
    newNodeContent: "",
    newNodePrefix: "",
    newNodeSuffix: "",
    decisionData: {
      conditions: [{ variable: "", operator: "==", value: "", type: "value", joiner: "and" }],
      logicType: "all",
      truePath: null,
      falsePath: null
    },
    sourceNodeId: null,
    newNodeEntityType: "USER",
    newNodeIdentifier: "",
    nextNodeIds: [],
    isDestinationNode: false,
    potentialStart: false,
    placeholders: {},
    isEditMode: false,
    nodeToEdit: null,
    nodeToDelete: null,
    hasToolCall: false,
    toolCalls: [],
    isPreviewModalVisible: false,
    previewNodeId: null,
    conversationPath: [],
    previewSessionId: generateSessionId(),
    currentPlaceholder: "",
    newExample: "",
    editNodeId: null,
    isNewVariable: true,
  });

  const [placeholderModalState, setPlaceholderModalState] = useState<PlaceholderModalState>({
    visible: false,             // Changed from isPlaceholderModalVisible
    currentPlaceholder: "",
    newExample: "",
    editNodeId: null,
    isNewVariable: true,
  });

  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    isDeleteNodeModalVisible: false,    // Separate flag for delete node modal
    isDeleteBranchModalVisible: false,  // Separate flag for delete branch modal
    nodeToDelete: null,         // Added
    nodeToDeleteFromMenu: null,
    deleteChildren: false,
  });

  const [previewModalState, setPreviewModalState] = useState<PreviewModalState>({
    visible: false,             // Changed from isPreviewModalVisible
    previewNodeId: null,
    conversationPath: [],
    conversationPaths: [],
    activePathIndex: 0,
    previewSessionId: generateSessionId(),
  });

  const [standaloneModalState, setStandaloneModalState] = useState<StandaloneModalState>({
    visible: false,             // Changed from isStandaloneModalVisible
    standaloneNodeEntityType: "USER",
    standaloneNodeContent: "",
    standaloneNodePrefix: "",
    standaloneNodeSuffix: "",
    standaloneDecisionData: {
      conditions: [{ variable: "", operator: "==", value: "", type: "value", joiner: "and" }],
      logicType: "all",
      truePath: null,
      falsePath: null
    },
    standaloneIdentifier: "",
    standaloneIsDestination: false,
    standalonePotentialStart: false,
    standaloneHasToolCall: false,
    standaloneToolCalls: [],
    standaloneVariableAssignments: [],
  });

  // Reset functions for each modal state
  const resetModalState = useCallback(() => {
    setModalState({
      visible: false,
      modalTitle: "Add New Node",
      newNodeContent: "",
      newNodePrefix: "",
      newNodeSuffix: "",
      decisionData: {
        conditions: [{ variable: "", operator: "==", value: "", type: "value", joiner: "and" }],
        logicType: "all",
        truePath: null,
        falsePath: null
      },
      sourceNodeId: null,
      newNodeEntityType: "USER",
      newNodeIdentifier: "",
      nextNodeIds: [],
      isDestinationNode: false,
      potentialStart: false,
      placeholders: {},
      isEditMode: false,
      nodeToEdit: null,
      nodeToDelete: null,
      hasToolCall: false,
      toolCalls: [],
      isPreviewModalVisible: false,
      previewNodeId: null,
      conversationPath: [],
      previewSessionId: generateSessionId(),
      currentPlaceholder: "",
      newExample: "",
      editNodeId: null,
      isNewVariable: true,
    });
  }, []);

  const resetPlaceholderModalState = useCallback(() => {
    setPlaceholderModalState({
      visible: false,
      currentPlaceholder: "",
      newExample: "",
      editNodeId: null,
      isNewVariable: true,
    });
  }, []);

  const resetDeleteModalState = useCallback(() => {
    setDeleteModalState({
      isDeleteNodeModalVisible: false,
      isDeleteBranchModalVisible: false,
      nodeToDelete: null,
      nodeToDeleteFromMenu: null,
      deleteChildren: false,
    });
  }, []);

  const resetPreviewModalState = useCallback(() => {
    setPreviewModalState({
      visible: false,
      previewNodeId: null,
      conversationPath: [],
      conversationPaths: [],
      activePathIndex: 0,
      previewSessionId: generateSessionId(),
    });
  }, []);

  const resetStandaloneModalState = useCallback(() => {
    setStandaloneModalState({
      visible: false,
      standaloneNodeEntityType: "USER",
      standaloneNodeContent: "",
      standaloneNodePrefix: "",
      standaloneNodeSuffix: "",
      standaloneDecisionData: {
        conditions: [{ variable: "", operator: "==", value: "", type: "value", joiner: "and" }],
        logicType: "all",
        truePath: null,
        falsePath: null
      },
      standaloneIdentifier: "",
      standaloneIsDestination: false,
      standalonePotentialStart: false,
      standaloneHasToolCall: false,
      standaloneToolCalls: [],
      standaloneVariableAssignments: [],
    });
  }, []);

  return {
    modalState,
    setModalState,
    placeholderModalState,
    setPlaceholderModalState,
    deleteModalState,
    setDeleteModalState,
    previewModalState,
    setPreviewModalState,
    standaloneModalState,
    setStandaloneModalState,
    isResetModalVisible,
    setIsResetModalVisible,
    isImportModalVisible,
    setIsImportModalVisible,
    isNewTreeModalVisible,
    setIsNewTreeModalVisible,
    searchText,
    setSearchText,
    resetModalState,
    resetPlaceholderModalState,
    resetDeleteModalState,
    resetPreviewModalState,
    resetStandaloneModalState,
  };
};